import { NoteViewer } from "@/components/note/viewer/note-viewer";
import { SpringAnimator } from "@/mds";
import { ItemPreviewHoveringState } from "./item-preview-hovering-state";
import { ZIndex } from "@/domains/design/constants";
import { css, keyframes } from "@/domains/emotion";
import { INoteObservable } from "@/store/note/types";
import { useAppStore } from "@/store/utils/hooks";
import { MDSNotePreviewContainer } from "@/mds/components/note-preview-container";
import { formatPreviewAttribution } from "@/domains/attribution/format-preview-attribution";
import {
  FloatingNode,
  FloatingPortal,
  Placement,
  flip,
  offset,
  safePolygon,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { AnimatePresence } from "motion/react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { MDSSpacings } from "@/mds/foundations/typography";

export const HEADS_UP_PREVIEW_PLACEMENT: Placement = "left-end";

export interface FloatingNotePreviewCardProps {
  state: ItemPreviewHoveringState;
  noteObservable?: INoteObservable;
  placement?: Placement;
  highlightText?: string;
  open?: boolean;
}

export const FloatingNotePreview = observer<FloatingNotePreviewCardProps>(
  ({ noteObservable, state, placement, highlightText, open }) => {
    const { store } = useAppStore();
    const nodeId = useFloatingNodeId();

    const isOpen = Boolean(noteObservable && (open ?? state.hovering));

    useEffect(() => {
      if (!isOpen || !noteObservable || noteObservable.isNoteContentDocumentLoaded) {
        return;
      }

      void noteObservable.fetchNoteContentDocument();
    }, [isOpen, noteObservable]);

    const resolvedPlacement = placement ?? "bottom-end";
    const isLeftPlacement = resolvedPlacement.startsWith("left");

    const middleware = useMemo(
      () => [
        offset({ mainAxis: 18 }),
        isLeftPlacement
          ? flip({ fallbackPlacements: ["left-start"], fallbackStrategy: "initialPlacement" })
          : flip(),
      ],
      [isLeftPlacement]
    );

    const { x, y, refs, strategy, context } = useFloating({
      nodeId,
      strategy: "fixed",
      middleware,
      open: isOpen,
      onOpenChange: value => {
        if (value) {
          /** We don't want to open on hover states. */
          return;
        }
      },
      placement: resolvedPlacement,
    });

    useEffect(() => {
      if (state.hoveredRect) {
        refs.setPositionReference({
          getBoundingClientRect() {
            return state.hoveredRect!;
          },
        });
      }
    }, [refs, state.hoveredRect]);

    const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context, {
        move: false,
        handleClose: safePolygon(),
        delay: {
          close: 100,
        },
      }),
      useDismiss(context),
    ]);

    const portalContent = useMemo(() => {
      if (!isOpen || !noteObservable) {
        return null;
      }

      // Attribution format ("Edited Aug 21 by Jess Joules" / "Created just now by Mem"),
      // matching the notes list.
      const secondaryLabel = buildPreviewAttribution(noteObservable);

      return (
        <SpringAnimator
          key={`floating-note-${nodeId}`}
          className={animationStyles}
          onClick={e => e.stopPropagation()}
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: ZIndex.FloatingNotePreview,
          }}
          {...getFloatingProps()}
          onMouseLeave={state.handleMouseLeave}
          onMouseEnter={state.handleMouseEnter}
        >
          <MDSNotePreviewContainer
            title={noteObservable.title}
            createdAt={noteObservable.locallyCreatedAt}
            modifiedAt={noteObservable.modifiedAt}
            authorNames={noteObservable.authors.map(author => author.profileDisplayName)}
            secondaryLabel={secondaryLabel}
            isShared={noteObservable.isShared}
            updateOnly={true}
            shouldScroll={false}
          >
            <NoteViewer
              className={editorStyles}
              noteObservable={noteObservable}
              goToMention={store.navigation.goToMention}
              showMetadata={false}
              highlightText={highlightText}
              disableGlobalListeners={true}
            />
          </MDSNotePreviewContainer>
        </SpringAnimator>
      );
    }, [
      getFloatingProps,
      highlightText,
      isOpen,
      nodeId,
      noteObservable,
      refs.setFloating,
      state.handleMouseEnter,
      state.handleMouseLeave,
      store,
      strategy,
      x,
      y,
    ]);

    // Don't render anything if we don't have a note to show
    if (!noteObservable) {
      return null;
    }

    return (
      <FloatingNode id={nodeId}>
        <div ref={refs.setReference} {...getReferenceProps()}></div>
        {isOpen && (
          <FloatingPortal id={`floating-note-preview-${nodeId}`}>
            <AnimatePresence mode="wait" initial={false}>
              {portalContent}
            </AnimatePresence>
          </FloatingPortal>
        )}
      </FloatingNode>
    );
  }
);

/**
 * Builds the footer second-row attribution string for a note preview.
 * Prefers NoteObservable.lastAction (single most-recent actor, with Mem-echo
 * suppression etc. already applied). Falls back to attributing the note to its
 * owner when lastAction is suppressed (e.g. your own private, unshared note).
 */
const buildPreviewAttribution = (noteObservable: INoteObservable): string | undefined => {
  const lastAction = noteObservable.lastAction;
  if (lastAction) {
    const authorName = lastAction.isMe ? "You" : lastAction.contact.profileDisplayName;
    return formatPreviewAttribution({
      verb: lastAction.verb,
      dateInput: lastAction.at,
      authorName,
    });
  }

  // Fallback: attribute to the owner, deriving the verb from whether the note
  // has been modified since creation.
  const owner = noteObservable.primaryOwner;
  if (!owner) {
    return undefined;
  }
  const createdAt = noteObservable.locallyCreatedAt;
  const modifiedAt = noteObservable.modifiedAt;
  const wasEdited = Boolean(modifiedAt && modifiedAt !== createdAt);
  const authorName = noteObservable.isOwnedByMe ? "You" : owner.profileDisplayName;
  return formatPreviewAttribution({
    verb: wasEdited ? "edited" : "created",
    dateInput: wasEdited ? modifiedAt! : createdAt,
    authorName,
  });
};

const growAndFadeAnimation = keyframes({
  from: {
    opacity: 0,
    transform: "scale(0.97)",
  },
  to: {
    opacity: 1,
    transform: "scale(1)",
  },
});

const animationStyles = css({
  width: "400px",
  animation: `${growAndFadeAnimation} .2s ease-out`,
});

const notePreviewContentPadding = MDSSpacings["4"];

const editorStyles = css({
  height: "100%",
  minHeight: 0,
  overflowY: "auto",
  scrollbarWidth: "none",
  "--extra-padding-top": notePreviewContentPadding,
  "--extra-padding-right": notePreviewContentPadding,
  "--extra-padding-bottom": notePreviewContentPadding,
  "--extra-padding-left": notePreviewContentPadding,
  "::-webkit-scrollbar": {
    display: "none",
  },
});
